/**
 * Редактор текста бота — /messages/{slug}.
 *
 * Layout:
 *   Слева: текстовое поле с HTML + список плейсхолдеров (можно вставить кликом)
 *   Справа: live-preview (рендер с тестовыми значениями)
 *
 * HTML — Telegram-safe subset (b/i/u/s/code/pre/a/blockquote + br/p).
 * При сохранении version++, audit-log запись.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { useShow } from "@refinedev/core";
import {
  Card, Row, Col, Space, Button, Tag, Typography, Input, Form,
  Skeleton, Result, Empty, Switch, message as antMessage, Alert,
  Tooltip, App as AntApp,
} from "antd";
import {
  ArrowLeftOutlined, SaveOutlined, EyeOutlined,
  CopyOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { API_URL } from "../../dataProvider";

interface Placeholder {
  name: string;
  example: string;
  description?: string;
}
interface BotMessageDetail {
  slug: string;
  title: string;
  description: string | null;
  category: string;
  parse_mode: string;
  enabled: boolean;
  version: number;
  updated_at: string | null;
  html: string;
  placeholders: Placeholder[];
  created_at: string | null;
}

const CATEGORY_META: Record<string, { color: string; label: string }> = {
  trial:    { color: "gold",    label: "🎁 Trial" },
  referral: { color: "purple",  label: "👥 Партнёрка" },
  fallback: { color: "magenta", label: "🔒 Fallback" },
  buy:      { color: "blue",    label: "🛒 Покупка" },
  common:   { color: "default", label: "📦 Общее" },
};

export const BotMessagesEdit = () => {
  const { id: slug } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { modal, message } = AntApp.useApp();

  const { query } = useShow<BotMessageDetail>();
  const data = query?.data?.data;

  // Локальное состояние формы
  const [html, setHtml] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [dirty, setDirty] = useState(false);

  // Подсасываем из API при загрузке
  useEffect(() => {
    if (data) {
      setHtml(data.html);
      setTitle(data.title);
      setDescription(data.description || "");
      setEnabled(data.enabled);
      setDirty(false);
    }
  }, [data]);

  // Live-preview: debounced рендер через API (использует example-значения)
  const refreshPreview = useCallback(async (currentHtml: string) => {
    if (!slug) return;
    try {
      const r = await fetch(`${API_URL}/admin/messages/${slug}/preview`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placeholders: {} }),
      });
      if (r.ok) {
        const j = await r.json();
        // если html в БД не обновлён — используем локальный из стейта
        // (preview-endpoint всегда отдаёт версию из БД, поэтому делаем локально)
        const safeFill = currentHtml.replace(
          /\{(\w+)\}/g,
          (_m, name) => {
            const ph = (data?.placeholders || []).find(p => p.name === name);
            return ph?.example || `{${name}}`;
          },
        );
        setPreviewHtml(safeFill);
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, data?.placeholders]);

  useEffect(() => {
    const t = setTimeout(() => refreshPreview(html), 300);
    return () => clearTimeout(t);
  }, [html, refreshPreview]);

  // Вставить плейсхолдер в текст в позицию каретки
  const insertPlaceholder = (name: string) => {
    const ta = document.getElementById("bm-html-ta") as HTMLTextAreaElement | null;
    if (!ta) {
      setHtml(html + `{${name}}`);
      setDirty(true);
      return;
    }
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const before = html.slice(0, start);
    const after = html.slice(end);
    const newVal = `${before}{${name}}${after}`;
    setHtml(newVal);
    setDirty(true);
    // Восстанавливаем курсор после вставки
    setTimeout(() => {
      ta.focus();
      const pos = start + name.length + 2;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  // Вставить HTML-теги вокруг выделения
  const wrapTag = (open: string, close: string) => {
    const ta = document.getElementById("bm-html-ta") as HTMLTextAreaElement | null;
    if (!ta) return;
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const selected = html.slice(start, end);
    const before = html.slice(0, start);
    const after = html.slice(end);
    const inserted = `${open}${selected}${close}`;
    setHtml(`${before}${inserted}${after}`);
    setDirty(true);
    setTimeout(() => {
      ta.focus();
      const pos = start + open.length + selected.length;
      ta.setSelectionRange(start + open.length, pos);
    }, 0);
  };

  const onSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/admin/messages/${slug}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ html, title, description, enabled }),
      });
      if (r.ok) {
        const j = await r.json();
        message.success(`Сохранено (версия ${j.version})`);
        setDirty(false);
        query?.refetch();
      } else {
        const t = await r.text();
        message.error(`Ошибка ${r.status}: ${t.slice(0, 200)}`);
      }
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    if (!data) return;
    modal.confirm({
      title: "Отменить несохранённые изменения?",
      content: "Все правки будут потеряны и форма вернётся к сохранённой версии из БД.",
      okText: "Отменить правки", okButtonProps: { danger: true },
      onOk: () => {
        setHtml(data.html);
        setTitle(data.title);
        setDescription(data.description || "");
        setEnabled(data.enabled);
        setDirty(false);
        message.info("Форма сброшена");
      },
    });
  };

  if (query?.isLoading) {
    return <Card><Skeleton active paragraph={{ rows: 12 }} /></Card>;
  }
  if (query?.isError) {
    const e: any = query?.error;
    if (e?.statusCode === 404) {
      return (
        <Result status="404" title="Текст не найден"
          subTitle={`Сообщение с slug="${slug}" отсутствует в БД.`}
          extra={<Button type="primary" onClick={() => nav("/messages")}>
            ← К списку текстов
          </Button>} />
      );
    }
    return (
      <Result status="error" title="Не удалось загрузить"
        subTitle={String(e?.message || e)}
        extra={<Button onClick={() => query?.refetch()}>Повторить</Button>} />
    );
  }
  if (!data) return <Empty description="Нет данных" />;

  const cat = CATEGORY_META[data.category] || CATEGORY_META.common;

  return (
    <div style={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12,
        alignItems: "center", justifyContent: "space-between",
        marginBottom: 16,
      }}>
        <Space size={12} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => nav("/messages")}>
            К списку
          </Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            ✏️ {data.title}
          </Typography.Title>
          <Tag color={cat.color}>{cat.label}</Tag>
          <Typography.Text code style={{ fontSize: 11 }}>{slug}</Typography.Text>
          <Tag>v{data.version}</Tag>
        </Space>
        <Space size={6} wrap>
          {dirty && (
            <Tag color="warning">Есть несохранённые изменения</Tag>
          )}
          <Button icon={<ReloadOutlined />} onClick={onReset} disabled={!dirty}>
            Сбросить
          </Button>
          <Button type="primary" icon={<SaveOutlined />}
            loading={saving} disabled={!dirty} onClick={onSave}>
            Сохранить
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {/* Левая колонка — редактор */}
        <Col xs={24} lg={14}>
          <Card title="✏️ Содержимое (HTML)" size="small">
            {/* Toolbar: bold/italic/underline/code + плейсхолдеры */}
            <Space style={{ marginBottom: 8 }} wrap>
              <Tooltip title="Жирный">
                <Button size="small" onClick={() => wrapTag("<b>", "</b>")}>
                  <b>B</b>
                </Button>
              </Tooltip>
              <Tooltip title="Курсив">
                <Button size="small" onClick={() => wrapTag("<i>", "</i>")}>
                  <i>I</i>
                </Button>
              </Tooltip>
              <Tooltip title="Подчёркивание">
                <Button size="small" onClick={() => wrapTag("<u>", "</u>")}>
                  <u>U</u>
                </Button>
              </Tooltip>
              <Tooltip title="Зачёркнутый">
                <Button size="small" onClick={() => wrapTag("<s>", "</s>")}>
                  <s>S</s>
                </Button>
              </Tooltip>
              <Tooltip title="Моноширинный (code)">
                <Button size="small" onClick={() => wrapTag("<code>", "</code>")}>
                  &lt;/&gt;
                </Button>
              </Tooltip>
              {/* Telegram-HTML НЕ поддерживает <br> и <p>. Используем
                  обычные \n переносы строк (Enter в textarea). */}
              <Tooltip title="Ссылка">
                <Button size="small" onClick={() => wrapTag('<a href="https://">', "</a>")}>
                  🔗
                </Button>
              </Tooltip>
            </Space>
            <Input.TextArea
              id="bm-html-ta"
              value={html}
              onChange={e => { setHtml(e.target.value); setDirty(true); }}
              rows={18}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: 13 }}
              placeholder="<p>Привет, {first_name}!</p>"
            />

            {/* Метаданные */}
            <Form layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item label="Название (для админки)">
                <Input value={title} onChange={e => { setTitle(e.target.value); setDirty(true); }} />
              </Form.Item>
              <Form.Item label="Описание (когда отправляется, контекст)">
                <Input.TextArea value={description}
                  onChange={e => { setDescription(e.target.value); setDirty(true); }}
                  rows={2} />
              </Form.Item>
              <Form.Item label="Статус">
                <Switch checked={enabled}
                  onChange={v => { setEnabled(v); setDirty(true); }}
                  checkedChildren="Активен" unCheckedChildren="Отключён" />
                <Typography.Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
                  Отключённый текст не используется ботом (заменяется fallback'ом из кода).
                </Typography.Text>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Правая колонка — preview + плейсхолдеры */}
        <Col xs={24} lg={10}>
          {/* Список плейсхолдеров */}
          {data.placeholders && data.placeholders.length > 0 && (
            <Card title="📝 Доступные плейсхолдеры" size="small"
              style={{ marginBottom: 16 }}>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                Кликните, чтобы вставить в текст в позицию курсора. Бот подставит
                реальные значения при отправке юзеру.
              </Typography.Paragraph>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                {data.placeholders.map(p => (
                  <div key={p.name} style={{
                    padding: 8,
                    borderRadius: 6,
                    background: "#fafafa",
                    border: "1px solid #f0f0f0",
                  }}>
                    <Space size={4} style={{ width: "100%", justifyContent: "space-between" }}>
                      <Space size={4}>
                        <Tooltip title="Вставить">
                          <Button size="small" type="link"
                            icon={<CopyOutlined />}
                            onClick={() => insertPlaceholder(p.name)}>
                            <Typography.Text code style={{ fontSize: 12 }}>
                              {`{${p.name}}`}
                            </Typography.Text>
                          </Button>
                        </Tooltip>
                      </Space>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        пример: «{p.example}»
                      </Typography.Text>
                    </Space>
                    {p.description && (
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
                        {p.description}
                      </Typography.Text>
                    )}
                  </div>
                ))}
              </Space>
            </Card>
          )}

          {/* Live preview */}
          <Card title={
            <Space>
              <EyeOutlined />
              <span>Превью (как видит юзер)</span>
            </Space>
          } size="small">
            <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
              Плейсхолдеры заполнены примерами. Реальный юзер получит свои значения.
            </Typography.Text>
            <div style={{
              padding: "12px 16px",
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e0e0e0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              maxWidth: "85%",
              minHeight: 100,
            }}>
              <div className="bemli-tg-preview"
                style={{ wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: previewHtml || "<i>(пусто)</i>" }} />
            </div>
          </Card>

          {/* HTML hint */}
          <Alert
            type="info" showIcon
            style={{ marginTop: 16 }}
            message="Допустимые HTML-теги Telegram"
            description={
              <div style={{ fontSize: 12 }}>
                <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;u&gt;</code>,
                <code>&lt;s&gt;</code>, <code>&lt;code&gt;</code>, <code>&lt;pre&gt;</code>,
                <code>&lt;a href=&quot;...&quot;&gt;</code>, <code>&lt;blockquote&gt;</code>.
                <br />
                <b>Переносы строк:</b> используйте обычный Enter (символ <code>\n</code>),
                не <code>&lt;br&gt;</code> и не <code>&lt;p&gt;</code>.
                <br />
                Эти теги Telegram <b>не поддерживает</b> и сообщение не отправится.
              </div>
            }
          />
        </Col>
      </Row>
    </div>
  );
};
